import { LEAVE_TYPE_LABELS } from '@aegislab/shared';
import type { AggregatedMemberAttendance } from '@/shared/types';
import { downloadCsv } from '../../../shared/lib/exportCsv';
import { formatDurationHHMM as msToHHMM } from '@/shared/lib/format';

const HEADERS = [
  '이름',
  '팀',
  '출근일',
  '결근',
  '지각',
  LEAVE_TYPE_LABELS.ANNUAL,
  '잔여연차',
  LEAVE_TYPE_LABELS.SICK,
  LEAVE_TYPE_LABELS.SPECIAL,
  '총 휴가',
  '총 근무시간',
  '초과근무',
  '야간근무',
  '야간초과근무',
  '휴일근무',
  // 인턴 주휴 발생 주 수 (영업일 WORK 주 10h↑ 충족 주). 비인턴은 '-'.
  '주휴 발생',
  // 정규직 전환일 기준 근무 구간 분리 (인턴구간 → 정규직구간)
  '인턴구간 출근일',
  '인턴구간 근무시간',
  '인턴구간 초과근무',
  '인턴구간 야간근무',
  '인턴구간 야간초과근무',
  '인턴구간 휴일근무',
  '정규직구간 출근일',
  '정규직구간 근무시간',
  '정규직구간 초과근무',
  '정규직구간 야간근무',
  '정규직구간 야간초과근무',
  '정규직구간 휴일근무',
];

export function exportAttendanceAggregate(
  data: AggregatedMemberAttendance[],
  teamNameMap: Map<string, string>,
  dateRange: { startDate: string; endDate: string }
): void {
  const rows = data.map((m) => {
    const nightOvertimeMs = m.totalNightOvertimeMs ?? 0;
    // 구간별 야간초과 — 초과/야간 컬럼의 겹침 제거(기존 합계 컬럼과 동일 정의)에 사용.
    const iNightOt = m.internWork?.totalNightOvertimeMs ?? 0;
    const fNightOt = m.fulltimeWork?.totalNightOvertimeMs ?? 0;
    return [
      m.name,
      m.teamId ? (teamNameMap.get(m.teamId) ?? '미배정') : '미배정',
      String(m.workDays),
      String(m.absentDays),
      String(m.lateDays),
      String(m.leaveByType.annual ?? 0),
      m.remainingAnnualLeave != null ? String(m.remainingAnnualLeave) : '-',
      String(m.leaveByType.sick ?? 0),
      String(m.leaveByType.special ?? 0),
      String(m.leaveDays),
      msToHHMM(m.totalWorkMs),
      msToHHMM(m.totalOvertimeMs - nightOvertimeMs),
      msToHHMM(m.totalNightMs - nightOvertimeMs),
      msToHHMM(nightOvertimeMs),
      msToHHMM(m.totalHolidayWorkMs),
      // 주휴 발생 — 인턴만 산출(eligibleWeeks), 비인턴은 '-'
      m.weeklyHolidayPay != null ? String(m.weeklyHolidayPay.eligibleWeeks) : '-',
      // 인턴구간 근무 (초과·야간은 야간초과를 제외해 겹침 없이 표시)
      String(m.internWork?.workDays ?? 0),
      msToHHMM(m.internWork?.totalWorkMs ?? 0),
      msToHHMM((m.internWork?.totalOvertimeMs ?? 0) - iNightOt),
      msToHHMM((m.internWork?.totalNightMs ?? 0) - iNightOt),
      msToHHMM(iNightOt),
      msToHHMM(m.internWork?.totalHolidayWorkMs ?? 0),
      // 정규직구간 근무
      String(m.fulltimeWork?.workDays ?? 0),
      msToHHMM(m.fulltimeWork?.totalWorkMs ?? 0),
      msToHHMM((m.fulltimeWork?.totalOvertimeMs ?? 0) - fNightOt),
      msToHHMM((m.fulltimeWork?.totalNightMs ?? 0) - fNightOt),
      msToHHMM(fNightOt),
      msToHHMM(m.fulltimeWork?.totalHolidayWorkMs ?? 0),
    ];
  });

  const filename = `근태집계_${dateRange.startDate}_${dateRange.endDate}.csv`;
  downloadCsv(filename, HEADERS, rows);
}
